import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./Negotiation.css";
import "./ManageNegotiation.css";
import {
  getNegotiationList,
  downloadNegotiation,
  negotiationDetails,
  createEnvelope,
  updateEnvelopeRecipientStep,
} from "../../../services/CommonService";

import NegotiationDelete from "./components/NegotiationDelete";
import { useNegotiation } from "../../../context/NegotiationProvider";
import TemplatePreviewModal from "./components/TemplatePreviewModal";
import ResendNegotiationModal from "./components/ResendNegotiationModal";
import FilterBar from "../Envelope/Manage/components/FilterBar";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import sortBy from "sort-by";
import ReactPaginate from "react-paginate";
import Utils from "../../../utils";

const STATUS_OPTIONS = [
  { value: "All Status", label: "All Status", color: "all-status" },
  { value: "Draft", label: "Draft", color: "draft" },
  { value: "Under Review", label: "Under Review", color: "under-review" },
  { value: "Approved", label: "Approved", color: "approved" },
  { value: "Send", label: "Send", color: "send" },
  { value: "Complete", label: "Complete", color: "complate" },
];

const STATUS_MAP = {
  1: { label: "Draft", className: "manage-status-draft" },
  2: { label: "Under Review", className: "manage-status-review" },
  3: { label: "Approved", className: "manage-status-approved" },
  4: { label: "Send", className: "manage-status-send" },
  5: { label: "Complete", className: "manage-status-complate" },
};

const STATUS_TO_ID = {
  Draft: 1,
  "Under Review": 2,
  Approved: 3,
  Send: 4,
  Complete: 5,
};

const DEBOUNCE_DELAY = 800;
const DEFAULT_ITEMS_PER_PAGE = 10;
const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50];
const MAX_VISIBLE_RECIPIENTS = 2;

function parseDdMmYyyy(dateStr) {
  if (!dateStr || !dateStr.includes("/")) return new Date(0);
  const [dd, mm, yyyy] = dateStr.split("/");
  return new Date(`${yyyy}-${mm}-${dd}`);
}

const getStatusLabel = (id) => STATUS_MAP[id]?.label || "Unknown";
const getStatusClassName = (id) =>
  STATUS_MAP[id]?.className || "manage-status-unknown";
const convertStatusToId = (label) => STATUS_TO_ID[label] || null;

const downloadPdfFromBase64 = (base64, filename) => {
  const bytes = atob(base64)
    .split("")
    .map((c) => c.charCodeAt(0));
  const blob = new Blob([new Uint8Array(bytes)], {
    type: "application/pdf",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const highlightSearchText = (text = "", query = "") => {
  if (!query) return text;
  const regex = new RegExp(`(${query})`, "gi");
  return text.split(regex).map((part, i) =>
    regex.test(part) ? <mark key={i}>{part}</mark> : part
  );
};

const generateDefaultClientMessage = (name) =>
  `Dear Collaborator,\n\nYou have been invited to review and sign the contract titled "${name}". Please review the contents carefully and sign before the due date.\n\nBest regards.`;

function getRecipientInitials(recipient) {
  return (
    ((recipient.first_name && recipient.first_name[0]) || "")
      .toUpperCase() +
    ((recipient.last_name && recipient.last_name[0]) || "").toUpperCase()
  );
}

function getRecipientBadgeClass(status) {
  if (status === 0) return "recipient-badge";
  if (status === 1) return "recipient-badge-not-submitted";
  if (status === 2) return "recipient-badge-approved";
  if (status === 4) return "recipient-badge-commented";
  return "recipient-badge";
}

const Negotiations = () => {
  const { setNegotiationData } = useNegotiation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [state, setState] = useState({
    searchText: "",
    statusFilter: "All Status",
    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    currentPage: 1,
    sortParams: { order_column: null, order_by: true },
    openDropdownId: null,
    deleteModalId: null,
    showResendModal: false,
    selectedRecipient: null,
    selectedNegotiation: null,
    showPreview: false,
    previewData: null,
    loading: false,
  });

  const updateState = (updates) =>
    setState((prev) => ({
      ...prev,
      ...updates,
    }));

  const {
    searchText,
    statusFilter,
    itemsPerPage,
    currentPage,
    sortParams,
    openDropdownId,
    deleteModalId,
    showResendModal,
    selectedRecipient,
    selectedNegotiation,
    showPreview,
    previewData,
    loading,
  } = state;

  const [debouncedSearch] = useDebounce(searchText.trim(), DEBOUNCE_DELAY);

  const {
    data: negotiationsData = [],
    isLoading: isListLoading,
    error: listError,
  } = useQuery({
    queryKey: ["negotiations", debouncedSearch, statusFilter],
    queryFn: async () => {
      const statusId = convertStatusToId(statusFilter);
      const payload = {
        search: debouncedSearch,
        ...(statusId && { status_id: statusId }),
      };
      const res = await getNegotiationList(payload);
      return res?.data?.data ?? [];
    },
    keepPreviousData: true,
  });

  useEffect(() => {
    if (listError) {
      toast.error(Utils.getErrorMessage(listError) || "Failed to load negotiations.");
    }
  }, [listError]);

  const { negotiations, totalRecords, totalPages } = useMemo(() => {
    let allData = [...negotiationsData];

    if (sortParams.order_column) {
      if (
        sortParams.order_column === "created_at" ||
        sortParams.order_column === "due_date"
      ) {
        allData.sort((a, b) => {
          const valA = parseDdMmYyyy(a[sortParams.order_column]);
          const valB = parseDdMmYyyy(b[sortParams.order_column]);
          return sortParams.order_by ? valA - valB : valB - valA;
        });
      } else {
        const key =
          (sortParams.order_by ? "" : "-") + sortParams.order_column;
        allData = allData.sort(sortBy(key));
      }
    }

    const totalRecordsLocal = allData.length;
    const totalPagesLocal =
      Math.ceil(totalRecordsLocal / itemsPerPage) || 1;

    let currentPageSafe = currentPage;
    if (currentPageSafe > totalPagesLocal) currentPageSafe = totalPagesLocal;
    if (currentPageSafe < 1) currentPageSafe = 1;

    const startIndex = (currentPageSafe - 1) * itemsPerPage;
    const paginatedData = allData.slice(
      startIndex,
      startIndex + itemsPerPage
    );

    return {
      negotiations: paginatedData,
      totalRecords: totalRecordsLocal,
      totalPages: totalPagesLocal,
    };
  }, [negotiationsData, sortParams, currentPage, itemsPerPage]);

  useEffect(() => {
    const tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach(function (tooltipTriggerEl) {
      if (window.bootstrap)
        new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, [negotiations, searchText, statusFilter, sortParams, currentPage, itemsPerPage]);

  const handleSort = (column) => {
    updateState({
      sortParams: {
        order_column: column,
        order_by:
          sortParams.order_column === column ? !sortParams.order_by : true,
      },
    });
  };

  const createEnvelopeFromNegotiation = async (n) => {
    updateState({ loading: true });
    try {
      const res = await negotiationDetails({ uuid: n.uuid });
      const nd = res?.data?.data ?? res?.data ?? res;

      if (!nd?.id) {
        toast.error(res?.data?.message || "Failed to fetch negotiation details.");
        return;
      }

      const msg = generateDefaultClientMessage(nd.negotiation_name);

      const envelopeName =
        nd.status_id === 3
          ? `${nd.negotiation_name}.`
          : nd.negotiation_name;

      const eRes = await createEnvelope({
        sender_id: nd.user_id,
        envelope_name: envelopeName,
        due_date: nd.due_date,
        message: msg,
        send_reminder: "0",
        over_due_reminder: "0",
        negotiation_id: nd.id,
        is_negotiation: true,
      });

      const eData = eRes?.data?.data ?? eRes?.data ?? eRes;
      if (!eData?.id && !eData?.uuid) {
        toast.error(eRes?.data?.message || "Failed to create sleeve.");
        return;
      }

      if (
        Array.isArray(nd.recipient_list) &&
        nd.recipient_list.length > 0
      ) {
        const ids = nd.recipient_list.map((r) => r.id).join(",");
        const uRes = await updateEnvelopeRecipientStep({
          envelope_id: eData.id ?? eData.envelope_id ?? null,
          recipient_ids: ids,
          email_subject: `Contract Review Request - ${envelopeName}`,
          email_text: msg,
        });
      }

      const id = eData.uuid ?? eData.envelope_uuid ?? eData.id;
      await new Promise((r) => setTimeout(r, 300));
      return {
        uuid: id,
        step: 2,
      };
    } catch (error) {
      toast.error(
        Utils.getErrorMessage(error) ||
        "Failed to create sleeve from negotiation."
      );
      throw new Error;
    } finally {
      updateState({ loading: false });
    }
  };

  const handleViewNegotiation = async (id) => {
    const n = negotiationsData.find((i) => i.id === id);

    updateState({ openDropdownId: null });

    if (!n) {
      toast.error("Negotiation not found");
      return;
    }

    if (n.status_id === 1) {
      return navigate(
        `/client-portal/negotiation-contract/create/${n.uuid}/${n.current_step}`
      );
    }

    if (n.status_id === 2) {
      return navigate(
        `/client-portal/negotiation-contract/view/${n.uuid}`
      );
    }

    if (n.status_id === 3) {
      if (n.envelope_uuid && n.envelope_step != null) {
        return navigate(
          `/client-portal/sleeve/edit/${n.envelope_uuid}/${Number(
            n.envelope_step
          ) + 1}`
        );
      }

      try {
        setNegotiationData({
          pdfUrl: n.pdf_url || null,
          negotiationId: n.id,
          name: n.negotiation_name,
        });

        const envelope = await createEnvelopeFromNegotiation(n);

        if (envelope?.uuid) {
          return navigate(
            `/client-portal/sleeve/edit/${envelope.uuid}/${Number(
              envelope.step
            ) + 1}`
          );
        }
      } catch (error) {
        toast.error(Utils.getErrorMessage(error));
      }
    }

    if (n.status_id === 4 || n.status_id === 5) {
      return navigate(`/manage/${n.envelope_uuid}`);
    }
  };

  const handleOpenDeleteModal = (id) => {
    updateState({ deleteModalId: id, openDropdownId: null });
    setTimeout(() => {
      const el = document.getElementById("deleteNegotiationModal");
      if (el && window.bootstrap)
        new window.bootstrap.Modal(el).show();
    }, 150);
  };

  const handleCloseDeleteModal = () => {
    updateState({ deleteModalId: null });
    const el = document.getElementById("deleteNegotiationModal");
    const modal = window.bootstrap?.Modal?.getInstance(el);
    if (modal) modal.hide();
  };

  const handleStatusFilterChange = (v) => {
    updateState({
      statusFilter: v,
      isStatusDropdownOpen: false,
      currentPage: 1,
    });
  };

  const handleNegotiationDeleted = (id) => {
    const key = ["negotiations", debouncedSearch, statusFilter];
    queryClient.setQueryData(key, (prev) => {
      if (!prev) return prev;
      const filtered = prev.filter((n) => n.id !== id);
      const newTotalPages =
        Math.ceil(filtered.length / itemsPerPage) || 1;
      if (currentPage > newTotalPages) {
        updateState({ currentPage: newTotalPages });
      }
      return filtered;
    });
  };

  const handleItemsPerPageChange = (n) => {
    updateState({
      itemsPerPage: Number(n),
      currentPage: 1,
    });
  };

  const handleNavigateToManageSleeve = () => navigate("/manage");
  const handleNavigateToManageNegotiationPage = () =>
    navigate("/manage/negotiation");

  const handleDownloadNegotiation = async (id, version) => {
    try {
      updateState({ openDropdownId: null, loading: true });
      const res = await downloadNegotiation({
        negotiation_id: id,
        version_number: version,
      });
      const data = res?.data?.data;
      if (!data?.pdf_base64 && !data?.pdf) {
        throw new Error("PDF data not available");
      }
      downloadPdfFromBase64(
        data.pdf_base64 || data.pdf,
        data.filename || `negotiation_${id}_v${version}.pdf`
      );
      toast.success(
        res?.data?.message || "Download successful!"
      );
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        Utils.getErrorMessage(error) ||
        "Failed to download PDF.";
      toast.error(errorMessage);
    } finally {
      updateState({ loading: false });
    }
  };

  const handlePreviewNegotiation = async (negotiation) => {
    try {
      updateState({ loading: true });
      updateState({ openDropdownId: null });
      const res = await negotiationDetails({ uuid: negotiation.uuid });
      const data = res?.data?.data ?? res?.data ?? {};

      if (!data?.html_content) {
        toast.error(
          res?.data?.message ||
          "No preview content available for this negotiation."
        );
        return;
      }

      updateState({
        previewData: {
          html_content: data.html_content,
          title: data.negotiation_name,
        },
        showPreview: true,
        showResendModal: false,
      });

      toast.success(
        res?.data?.message || "Preview loaded successfully."
      );
    } catch (error) {
      toast.error(
        Utils.getErrorMessage(error) || "Failed to load preview."
      );
    } finally {
      updateState({ loading: false });
    }
  };

  const createNegotiation = () => {
    navigate("/client-portal/negotiation-contract/create");
  };

  const getSortIcon = (column) => {
    if (sortParams.order_column !== column) {
      return <i className="bi bi-arrow-down-up"></i>;
    }
    if (sortParams.order_by) {
      return <i className="bi bi-arrow-up"></i>;
    }
    return <i className="bi bi-arrow-down"></i>;
  };

  const handleOpenResendModal = (negotiation, recipient = null) => {
    updateState({
      openDropdownId: null,
      selectedNegotiation: {
        id: negotiation.id,
        uuid: negotiation.uuid,
        negotiation_name: negotiation.negotiation_name,
        recipients: negotiation.recipients,
      },
      selectedRecipient: recipient,
      showResendModal: true,
    });
  };

  const handlePageChange = (selectedItem) => {
    updateState({ currentPage: selectedItem.selected + 1 });
  };

  const handleDropdownOptionClick = (option) => {

    handleStatusFilterChange(option.value);
  };
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      updateState({ searchText: e.target.value });
    }
  };

  return (
    <>
      {(loading || isListLoading) && (
        <div className="page-loading">
          <img src="/images/loader.gif" alt="Loading..." />
        </div>
      )}

      <section className="manage-main-wrapper manage-section-bg-color manage-negotiation-container">
        <div className="custom_container manage-custom-container">
          
          <FilterBar
            searchText={searchText}
            setSearchText={(val) => updateState({ searchText: val })}
            handleKeyDown={handleSearchKeyDown}
            handleManageSleeve={handleNavigateToManageSleeve}
            handleManageNegotiation={handleNavigateToManageNegotiationPage}
            createContract={createNegotiation}
            selectedOption={statusFilter}
            dropdownOptions={STATUS_OPTIONS}
            handleDropdownOptionClick={handleDropdownOptionClick}
            activeTab="negotiation"
          />

          <div className="manage-table-section">
            {/* Added overflowX to allow scrolling if widths exceed screen size */}
            <div className="manage-table-container" style={{ overflowX: "auto" }}>
              <table className="manage-table" style={{ minWidth: "1000px" }}>
                <thead className="manage-table-head">
                  <tr>
                    <th
                      className="manage-th manage-th-name sortable-header"
                      onClick={() => handleSort("negotiation_name")}
                      style={{ minWidth: "250px" }} // Added Min Width
                    >
                      <div className="th-content">
                        <span>Negotiation Name</span>
                        {getSortIcon("negotiation_name")}
                      </div>
                    </th>
                    <th className="manage-th manage-th-status text-center" style={{ minWidth: "130px" }}>Status</th> {/* Added Min Width */}
                    <th className="manage-th manage-th-version text-center" style={{ minWidth: "100px" }}>Version</th> {/* Added Min Width */}
                    <th
                      className="manage-th manage-th-date sortable-header"
                      onClick={() => handleSort("due_date")}
                      style={{ minWidth: "120px" }}
                    >
                      <div className="th-content">
                        <span>Due Date</span>
                        {getSortIcon("due_date")}
                      </div>
                    </th>
                    <th
                      className="manage-th manage-th-date sortable-header"
                      onClick={() => handleSort("created_at")}
                      style={{ minWidth: "120px" }}
                    >
                      <div className="th-content">
                        <span>Created</span>
                        {getSortIcon("created_at")}
                      </div>
                    </th>
                    <th className="manage-th manage-th-recipients" style={{ minWidth: "150px" }}>
                      Collaborator
                    </th>
                    <th className="manage-th manage-th-download text-center" style={{ minWidth: "100px" }}>Download</th>
                    <th className="manage-th manage-th-action text-center" style={{ minWidth: "100px" }}>Action</th>
                    <th className="manage-th manage-th-menu " style={{ minWidth: "50px" }}><i className="bi bi-three-dots-vertical d-flex justify-content-center align-items-center"></i></th>
                  </tr>
                </thead>
                <tbody className="manage-table-body">
                  {negotiations.length > 0 ? (
                    negotiations.map((n) => (
                      <tr key={n.id} className="manage-table-row">
                        <td className="manage-td manage-td-name" style={{ whiteSpace: "nowrap" }}>
                          <div
                            onClick={() => handleViewNegotiation(n.id)}
                            className="manage-negotiation-name"
                          >
                            <span className="manage-name-text">
                              {highlightSearchText(
                                n.negotiation_name,
                                searchText
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="manage-td manage-td-status text-center">
                          <span
                            className={`manage-badge ${getStatusClassName(
                              n.status_id
                            )}`}
                          >
                            {getStatusLabel(n.status_id)}
                          </span>
                        </td>
                        <td className="manage-td manage-td-version text-center">
                          <span className="manage-version-badge">
                            v{n.current_version}
                          </span>
                        </td>
                        <td className="manage-td manage-td-date" style={{ whiteSpace: "nowrap" }}>
                          <span className="manage-date-text">
                            {n.due_date
                              ? parseDdMmYyyy(
                                n.due_date
                              ).toLocaleDateString()
                              : "-"}
                          </span>
                        </td>
                        <td className="manage-td manage-td-date" style={{ whiteSpace: "nowrap" }}>
                          <span className="manage-date-text manage-created-date">
                            {n.created_at
                              ? parseDdMmYyyy(
                                n.created_at
                              ).toLocaleDateString()
                              : "-"}
                          </span>
                        </td>
                        <td className="manage-td manage-td-recipients text-center">
                          <div className="manage-recipients-list ">
                            {Array.isArray(n.recipients) &&
                              n.recipients.length > 0 ? (
                              <>
                                {n.recipients
                                  .slice(0, MAX_VISIBLE_RECIPIENTS)
                                  .map((r) => (
                                    <div
                                      key={r.id}
                                      className={`manage-recipient-badge ${getRecipientBadgeClass(
                                        r.status
                                      )} me-1`}
                                      title={`${r.first_name} ${r.last_name}`}
                                      onClick={() =>
                                        handleOpenResendModal(n, r)
                                      }
                                      style={{ cursor: "pointer" }}
                                    >
                                      {getRecipientInitials(r)}
                                    </div>
                                  ))}
                                {n.recipients.length >
                                  MAX_VISIBLE_RECIPIENTS && (
                                    <span className="manage-recipient-more">
                                      +
                                      {n.recipients.length -
                                        MAX_VISIBLE_RECIPIENTS}
                                    </span>
                                  )}
                              </>
                            ) : (
                              <span className="manage-no-recipients">
                                -
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          className="manage-td manage-td-download text-center"
                          style={{
                            overflow: "visible",
                            position: "relative",
                          }}
                        >
                          {n.status_id === 1 ? (
                            <span
                              className="px-2 py-1 btn-download"
                              style={{
                                fontSize: "16px",
                                opacity: 0.5,
                                cursor: "not-allowed",
                              }}
                            >
                              <i className="bi bi-download"></i>
                            </span>
                          ) : n.current_version > 1 ? (
                            <div
                              className="dropdown"
                              style={{
                                position: "relative",
                                display: "inline-block",
                                width: "auto",
                              }}
                            >
                              <span
                                className="px-2 py-1 btn-download"
                                id={`dropdownMenuButton-${n.id}`}
                                data-bs-toggle="dropdown"
                                data-bs-popper-config='{"strategy":"fixed"}'
                                aria-expanded="false"
                                style={{
                                  fontSize: "16px",
                                }}
                              >
                                <i className="bi bi-download"></i>
                              </span>
                              <ul
                                className="dropdown-menu"
                                aria-labelledby={`dropdownMenuButton-${n.id}`}
                                style={{
                                  minWidth: "80px",
                                }}
                              >
                                {Array.from(
                                  { length: n.current_version },
                                  (_, i) => i + 1
                                )
                                  .filter(
                                    (v) =>
                                      n.status_id !== 2 ||
                                      v > n.current_version - 2
                                  )
                                  .map((v) => (
                                  <li key={v}>
                                    <button
                                      className="dropdown-item"
                                      onClick={() =>
                                        handleDownloadNegotiation(n.id, v)
                                      }
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      {`v${v}`}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <span
                              className="px-2 py-1 btn-download"
                              onClick={() =>
                                handleDownloadNegotiation(n.id, 1)
                              }
                              style={{
                                fontSize: "16px",
                              }}
                            >
                              <i className="bi bi-download"></i>
                            </span>
                          )}
                        </td>
                        <td className="manage-td manage-td-action">
                          <button
                            type="button"
                            className="manage-action-btn"
                            onClick={() => handleViewNegotiation(n.id)}
                          >
                            Manage
                          </button>
                        </td>
                        <td className="manage-td manage-td-menu">
                          <div className="dropdown d-flex align-items-center justify-content-center">
                            <button
                              className=" d-flex justify-content-center align-items-center bg-transparent"
                              type="button"
                              data-bs-toggle="dropdown"
                              data-bs-popper-config='{"strategy":"fixed"}'
                              aria-expanded="false"
                            >
                              <i className="bi bi-three-dots-vertical"></i>
                            </button>

                            <ul className="dropdown-menu dropdown-menu-end">
                              <li>
                                <button className="dropdown-item" onClick={() => handlePreviewNegotiation(n)}>
                                  <i className="bi bi-eye me-2"></i> Preview
                                </button>
                              </li>

                              {!([1, 3, 5].includes(n.status_id)) && (
                                <li>
                                  <button className="dropdown-item" onClick={() => handleOpenResendModal(n)}>
                                    <i className="bi bi-arrow-repeat me-2"></i> Resend
                                  </button>
                                </li>
                              )}

                              <li>
                                <button className="dropdown-item text-danger" onClick={() => handleOpenDeleteModal(n.id)}>
                                  <i className="bi bi-trash me-2"></i> Delete
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="manage-empty-state" colSpan={9}>
                        <div className="manage-empty-content">
                          <i className="bi bi-inbox manage-empty-icon" />
                          <h5 className="manage-empty-title">
                            No Negotiations Created
                          </h5>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalRecords >= 10 && (
            <div className="manage-table-footer">
              <div className="manage-footer-content">
                <div className="manage-items-per-page">
                  <span className="manage-footer-text">Show</span>
                  <select
                    className="manage-page-select"
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(e.target.value)
                    }
                  >
                    {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>

                {totalPages > 1 && (
                  <ReactPaginate
                    previousLabel={"<"}
                    nextLabel={">"}
                    breakLabel={"..."}
                    breakClassName={"manage-pagination-break"}
                    pageCount={totalPages}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={handlePageChange}
                    containerClassName={"manage-pagination"}
                    subContainerClassName={"pages pagination"}
                    activeClassName={"manage-pagination-active"}
                    pageClassName={"manage-pagination-item"}
                    previousClassName={"manage-pagination-item"}
                    nextClassName={"manage-pagination-item"}
                    disabledClassName={"manage-pagination-disabled"}
                    forcePage={currentPage - 1}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <NegotiationDelete
        show={!!deleteModalId}
        id={deleteModalId}
        onClose={handleCloseDeleteModal}
        onDeleted={handleNegotiationDeleted}
      />
      {showPreview && (
        <TemplatePreviewModal
          show={showPreview}
          onClose={() => updateState({ showPreview: false, previewData: null })}
          template={previewData}
        />
      )}
      <ResendNegotiationModal
        show={showResendModal}
        onClose={() => updateState({ showResendModal: false, selectedNegotiation: null, selectedRecipient: null })}
        negotiation={selectedNegotiation}
        recipient={selectedRecipient}
      />
    </>
  );
};

export default Negotiations;